/**
 * Merchant Initializer Component
 * Runs once on app load to restore/fetch merchant data
 * Rendered globally in layout to ensure merchant data is available on all pages
 */
import { useModel } from '@umijs/max';
import { useEffect, useRef } from 'react';
import { getActingAsMerchantsCache } from '@/models/actingAsMerchant';

const MerchantInitializer: React.FC = () => {
  const {
    initialState,
    setInitialState,
    loading: initialStateLoading,
  } = useModel('@@initialState');
  const { merchants, loading, fetchMerchants, loadMerchants, changeMerchant } =
    useModel('merchant');
  const initRef = useRef(false);

  useEffect(() => {
    const userId =
      initialState?.currentUser?.id || initialState?.currentUser?.userid;
    const role =
      initialState?.currentUser?.role || initialState?.currentUser?.access;

    // Only run once per user session when we have a user but no merchants loaded
    if (
      userId &&
      !initialStateLoading &&
      merchants.length === 0 &&
      !loading &&
      !initRef.current
    ) {
      initRef.current = true;

      // SuperAdmin in merchant owner mode: restore from sessionStorage cache
      if (role === 'SuperAdmin') {
        const cached = getActingAsMerchantsCache();
        if (cached.length > 0) {
          loadMerchants(cached, String(userId), cached[0].id);
          setInitialState((prev: any) => ({ ...prev }));
          return;
        }
        // No cache - don't fetch all partners for SuperAdmin
        return;
      }

      // Normal user: fetch from API
      fetchMerchants(userId).then(() => {
        // Trigger access re-evaluation by updating initialState
        setInitialState((prev: any) => ({ ...prev }));
      });
    }

    // Reset init flag when user changes (logout/login)
    if (!userId) {
      initRef.current = false;
    }
  }, [
    initialState?.currentUser,
    initialStateLoading,
    merchants.length,
    loading,
    fetchMerchants,
    loadMerchants,
    changeMerchant,
  ]);

  // This component renders nothing
  return null;
};

export default MerchantInitializer;
